import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook for a horizontally resizable panel that can flip between start/end sides.
 * Persists width and side preference to localStorage.
 *
 * @param {string} storageKey - Unique key for localStorage (e.g. 'liveMap', 'dispatch')
 * @param {object} opts - { defaultWidth, minWidth, maxWidth }
 * @returns {{ width: number, side: 'start'|'end', onDragStart: Function, onFlip: Function }}
 */
export default function useResizablePanel(storageKey, opts = {}) {
  const { defaultWidth = 380, minWidth = 250, maxWidth = 700 } = opts;

  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem(`trackizy_panel_${storageKey}_width`);
    return saved ? Math.max(minWidth, Math.min(maxWidth, Number(saved))) : defaultWidth;
  });

  const [side, setSide] = useState(() => {
    return localStorage.getItem(`trackizy_panel_${storageKey}_side`) || 'start';
  });

  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);
  const sideRef = useRef(side);
  sideRef.current = side;

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(`trackizy_panel_${storageKey}_width`, String(width));
  }, [storageKey, width]);

  useEffect(() => {
    localStorage.setItem(`trackizy_panel_${storageKey}_side`, side);
  }, [storageKey, side]);

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = width;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  useEffect(() => {
    const effectiveMax = Math.min(maxWidth, window.innerWidth * 0.6);

    const onMove = (e) => {
      if (!dragging.current) return;
      const isRTL = document.documentElement.dir === 'rtl';
      const isStart = sideRef.current === 'start';
      // Dragging outward from panel edge = wider
      let delta = e.clientX - startX.current;
      if (isRTL) delta = -delta;
      if (!isStart) delta = -delta;
      setWidth(Math.max(minWidth, Math.min(effectiveMax, startW.current + delta)));
    };

    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [minWidth, maxWidth]);

  const onFlip = useCallback(() => {
    setSide((s) => (s === 'start' ? 'end' : 'start'));
  }, []);

  return { width, side, onDragStart, onFlip };
}
